import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ImagePlus, X, Loader2, ShieldCheck, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, CATEGORIES, DISTRICTS, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";

async function downscale(file) {
  const dataUrl = await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const max = 1200;
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", 0.78));
    };
    img.src = dataUrl;
  });
}

export default function CreateListing() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEdit = Boolean(id);

  const [form, setForm] = useState({
    title: "", description: "", price: "", category: "", district: "",
    phone: user?.phone ? "0" + user.phone.slice(2) : "",
  });
  const [images, setImages] = useState([]);
  const [consentKvkk, setConsentKvkk] = useState(false);
  const [consentPublish, setConsentPublish] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(isEdit);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    api
      .get(`/listings/${id}`)
      .then((r) => {
        const l = r.data;
        setForm({
          title: l.title, description: l.description, price: String(l.price),
          category: l.category, district: l.district, phone: "0" + (l.phone || "").slice(2),
        });
        setImages(l.images || []);
        setLoadingEdit(false);
      })
      .catch(() => {
        toast.error("İlan bulunamadı.");
        navigate("/hesabim");
      });
  }, [id, isEdit, navigate]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []).filter((f) => f.type.startsWith("image/"));
    if (!files.length) return;
    const room = 4 - images.length;
    const picked = [];
    for (const f of files.slice(0, room)) {
      try {
        picked.push(await downscale(f));
      } catch (err) {
        toast.error("Fotoğraf işlenemedi: " + f.name);
      }
    }
    setImages((prev) => [...prev, ...picked]);
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!consentKvkk || !consentPublish) {
      toast.error("Devam etmek için zorunlu onay kutularını işaretleyin.");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      price: parseFloat(String(form.price).replace(/[^\d.,]/g, "").replace(",", ".")) || 0,
      images,
      consent_kvkk: consentKvkk,
      consent_publish: consentPublish,
    };
    try {
      if (isEdit) {
        await api.put(`/listings/${id}`, payload);
        toast.success("İlanınız güncellendi ve tekrar onaya gönderildi.");
      } else {
        await api.post("/listings", payload);
        toast.success("İlanınız onay kuyruğuna alındı.");
      }
      setDone(true);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loadingEdit) return <div className="grid min-h-[60vh] place-items-center pt-20"><Loader2 className="animate-spin text-coral" size={34} /></div>;

  if (done) {
    return (
      <div className="mx-auto max-w-xl px-4 pt-36 pb-24 text-center" data-testid="listing-submitted-success">
        <Reveal>
          <span className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-emerald-100 text-emerald-600">
            <CheckCircle2 size={40} />
          </span>
          <h1 className="mt-6 font-display text-3xl font-black tracking-tight">İlanın onay kuyruğunda</h1>
          <p className="mt-3 text-slate-600 leading-relaxed">
            Teşekkürler! İlanınız Alsat67 yetkili ekibi tarafından incelenip onaylandıktan sonra yayınlanacaktır.
            Sonucu <strong>Hesabım</strong> sayfasından takip edebilirsiniz.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link data-testid="success-my-listings-button" to="/hesabim" className="rounded-full bg-coral px-7 py-3 text-sm font-bold text-white hover:bg-coraldark transition">İlanlarımı Gör</Link>
            <Link data-testid="success-home-button" to="/" className="rounded-full border border-coal/15 px-7 py-3 text-sm font-bold hover:border-coral hover:text-coral transition">Ana Sayfa</Link>
          </div>
        </Reveal>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 pt-24 pb-20">
      <Reveal>
        <p className="font-mono text-xs font-bold tracking-widest text-coral">{isEdit ? "İLANI GÜNCELLE" : "YENİ İLAN"}</p>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-1">
          {isEdit ? "İlanını güncelle, tekrar onaya gönder" : "İlanını 1 dakikada ver"}
        </h1>
        <div className="mt-4 flex items-start gap-2.5 rounded-2xl border border-amber2/50 bg-amber2/10 px-4 py-3.5 text-[13px] font-medium text-amber-900">
          <ShieldCheck size={17} className="shrink-0 mt-0.5" />
          Tüm ilanlar moderasyon ekibimizce kontrol edilir. Uygunsuz içerikler yayınlanmaz; gerekirse size geri dönüş yapılır.
        </div>
      </Reveal>

      <form onSubmit={submit} className="mt-8 space-y-5" data-testid="create-listing-form">
        <Reveal delay={0.05}>
          <div className="rounded-3xl border border-black/5 bg-card p-6 space-y-5 shadow-sm">
            <div className="space-y-2">
              <Label htmlFor="title">İlan Başlığı *</Label>
              <Input id="title" data-testid="listing-title-input" required minLength={5} maxLength={90} value={form.title} onChange={(e) => set("title")(e.target.value)} placeholder="Örn: Az kullanılmış şehir bisikleti" />
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Kategori *</Label>
                <Select data-testid="listing-category-select" required value={form.category} onValueChange={set("category")}>
                  <SelectTrigger data-testid="listing-category-select-trigger"><SelectValue placeholder="Kategori seç" /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>İlçe *</Label>
                <Select data-testid="listing-district-select" required value={form.district} onValueChange={set("district")}>
                  <SelectTrigger data-testid="listing-district-select-trigger"><SelectValue placeholder="İlçe seç" /></SelectTrigger>
                  <SelectContent>
                    {DISTRICTS.map((d) => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="price">Fiyat (₺) *</Label>
                <Input id="price" data-testid="listing-price-input" required type="number" min="1" step="0.01" value={form.price} onChange={(e) => set("price")(e.target.value)} placeholder="1450" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">İletişim Telefonu (WhatsApp) *</Label>
                <Input id="phone" data-testid="listing-phone-input" required type="tel" value={form.phone} onChange={(e) => set("phone")(e.target.value)} placeholder="0532 123 45 67" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="desc">Açıklama *</Label>
              <Textarea id="desc" data-testid="listing-description-input" required minLength={10} rows={5} value={form.description} onChange={(e) => set("description")(e.target.value)} placeholder="Ürünün durumu, garanti, teslim şekli... En az 10 karakter." />
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="rounded-3xl border border-black/5 bg-card p-6 shadow-sm">
            <Label>Fotoğraflar (en fazla 4)</Label>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {images.map((img, i) => (
                <div key={i} className="relative aspect-square overflow-hidden rounded-xl border border-black/10 group">
                  <img src={img} alt={`Fotoğraf ${i + 1}`} className="h-full w-full object-cover" />
                  <button
                    type="button" data-testid={`remove-image-${i}`}
                    onClick={() => setImages((prev) => prev.filter((_, idx) => idx !== i))}
                    className="absolute right-1.5 top-1.5 grid h-7 w-7 place-items-center rounded-full bg-black/60 text-white hover:bg-coral transition"
                    aria-label="Fotoğrafı kaldır"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
              {images.length < 4 && (
                <label className="grid aspect-square cursor-pointer place-items-center rounded-xl border-2 border-dashed border-slate-300 text-slate-400 hover:border-coral hover:text-coral transition">
                  <span className="flex flex-col items-center gap-1.5 text-xs font-semibold px-2 text-center">
                    <ImagePlus size={22} /> Fotoğraf Ekle
                  </span>
                  <input data-testid="listing-image-input" type="file" accept="image/*" multiple className="hidden" onChange={onFiles} />
                </label>
              )}
            </div>
            <p className="mt-2 text-[11px] text-slate-400">Fotoğraflar otomatik olarak küçültülür. Ürünün net fotoğrafları onayı hızlandırır.</p>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="rounded-3xl border border-black/5 bg-card p-6 space-y-4 shadow-sm">
            <h3 className="font-display font-bold">Onay & KVKK İzinleri</h3>
            <div className="flex items-start gap-3">
              <Checkbox data-testid="kvkk-consent-checkbox" id="ckvkk" checked={consentKvkk} onCheckedChange={(v) => setConsentKvkk(Boolean(v))} className="mt-0.5" />
              <label htmlFor="ckvkk" className="text-[13px] leading-relaxed text-slate-600">
                <strong className="text-coal">(Zorunlu)</strong>{" "}
                <Link to="/kvkk-aydinlatma-metni" target="_blank" className="text-coral font-semibold underline underline-offset-2">KVKK Aydınlatma Metni</Link>{" "}
                kapsamda ad, telefon numarası ve ilan bilgilerimin, alıcıyla iletişim kurulması amacıyla işlenmesini ve ilan sayfasında görünmesini onaylıyorum.
              </label>
            </div>
            <div className="flex items-start gap-3">
              <Checkbox data-testid="publish-consent-checkbox" id="cpub" checked={consentPublish} onCheckedChange={(v) => setConsentPublish(Boolean(v))} className="mt-0.5" />
              <label htmlFor="cpub" className="text-[13px] leading-relaxed text-slate-600">
                <strong className="text-coal">(Zorunlu)</strong> İlanımın moderasyon onayından sonra herkese açık olarak yayınlanmasını ve{" "}
                <Link to="/kullanim-kosullari" target="_blank" className="text-coral font-semibold underline underline-offset-2">Kullanım Koşulları</Link>{" "}
                kurallarını kabul ediyorum. Yasa dışı, yanıltıcı veya uygunsuz ilanların reddedileceğini biliyorum.
              </label>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <Button
            data-testid="create-listing-submit-button"
            type="submit"
            disabled={saving || !consentKvkk || !consentPublish}
            className="w-full rounded-full bg-coral py-4 text-base font-bold text-white hover:bg-coraldark transition shadow-lg shadow-coral/30"
          >
            {saving ? <><Loader2 size={18} className="animate-spin" /> Gönderiliyor...</> : isEdit ? "Güncelle & Onaya Gönder" : "İlanı Onaya Gönder"}
          </Button>
        </Reveal>
      </form>
    </div>
  );
}
